import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { StateRxService as StateRx } from './staterx.service';
import { AutocompleteUiItem, AutocompleteSelectedItem } from '../components/search-input/search-input.model';

@Injectable()
export class StateService {
  // Stores the previous autocomplete results
  private static lastTopSearchResponse: AutocompleteUiItem;
  private static lastDefaultCategory: string;
  private static lastSearchType: string;
  private static lastSelected: AutocompleteSelectedItem;

  // Stores the current layout state
  public isMobileLayout: boolean = false;
  public isSavingTopResult = new BehaviorSubject(undefined);

  // Stores the search query parameters
  private searchQueries: any;

  // Stores GTM custom message
  private customMessage: any;

  constructor(private _stateRx: StateRx) {}

  keyPressed(keyCode: number) {
    if (keyCode === 27) {
      this._stateRx.escPressed.rxNext(keyCode); // Notify subscribers
    }
    if (keyCode === 38) {
      this._stateRx.arrowUpPressed.rxNext(keyCode);
    }
    if (keyCode === 40) {
      this._stateRx.arrowDownPressed.rxNext(keyCode);
    }
    if (keyCode === 13) {
      this._stateRx.enterPressed.rxNext(keyCode);
    }
  }

  // Flag indicating if component is loading data.
  isComponentLoading(isLoading: boolean) {
    this._stateRx.isLoading.rxNext(isLoading);
  }

  hasError(isError: boolean) {
    this._stateRx.hasError.rxNext(isError);
  }

  // Filters layout on the search results page have simpler css if we apply a mobile class to the parent app container.
  mobileLayoutChanged(isMobile: boolean) {
    this.isMobileLayout = isMobile;
    this._stateRx.mobileLayoutChanged.rxNext(isMobile);
  }

  // Currently used by 'Report incorrect details' component which is opened in a popup.
  closePopup() {
    this._stateRx.closePopup.rxNext(true);
  }

  findClick() {
    this._stateRx.findClick.rxNext(true);
  }

  toggleMapSidebar(location: any) {
    this._stateRx.toggleMapSidebar.rxNext(location);
  }

  openPopupMap(locations: any[]) {
    this._stateRx.openPopupMap.rxNext(locations);
  }

  // Stores the search query parameters and updates if any changes made
  setSearchQueryParameters(searchParameters: any) {
    this.searchQueries = searchParameters;
    this._stateRx.searchQueryParameters.rxNext(searchParameters);
  }

  // Gets the search query parameters
  getSearchQueryParameters() {
    return this.searchQueries;
  }

  /**
   * Save the top result from the previous search results for cross component usage
   */
  saveLastTopSearchResponse(response: AutocompleteUiItem) {
    StateService.lastTopSearchResponse = { ...response };
  }

  /**
   * Retrieve the top result from the previous search results for cross component usage
   */
  getLastTopSearchResponse() {
    return StateService.lastTopSearchResponse ? { ...StateService.lastTopSearchResponse } : undefined;
  }

  /**
   * Clear the previous top search result from autocomplete
   */
  clearLastTopSearchResponse() {
    StateService.lastTopSearchResponse = undefined;
  }

  /**
   * Save the previous determined default category for cross component usage
   */
  saveLastDefaultCategory(defaultCategory: string) {
    StateService.lastDefaultCategory = defaultCategory;
  }

  /**
   * Retrieve the default category from the previous search results for cross component usage
   */
  getLastDefaultCategory() {
    return StateService.lastDefaultCategory;
  }

  /**
   * Clear the previous default category set
   */
  clearLastDefaultCategory() {
    StateService.lastDefaultCategory = undefined;
  }

  /**
   * Save the previous determined search type for cross component usage
   */
  saveLastSearchType(searchType: string) {
    StateService.lastSearchType = searchType;
  }

  /**
   * Retrieve the search type from the previous search results for cross component usage
   */
  getLastSearchType() {
    return StateService.lastSearchType;
  }

  /**
   * Clear the previous search type set
   */
  clearLastSearchType() {
    StateService.lastSearchType = undefined;
  }

  /**
   * Save the previously selected item for cross component usage
   */
  saveLastSelected(lastSelected: AutocompleteSelectedItem) {
    StateService.lastSelected = { ...lastSelected };
  }

  /**
   * Retrieve the previously selected item from the previous search results for cross component usage
   */
  getLastSelected() {
    return StateService.lastSelected ? { ...StateService.lastSelected } : undefined;
  }

  /**
   * Clear the previous search type set
   */
  clearLastSelected() {
    StateService.lastSelected = undefined;
  }

  // Stores a GTM custom message and updates if it changes
  setCustomMessage(message: any) {
    this.customMessage = message;
    this._stateRx.customMessage.rxNext(message);
  }

  // Remove GTM custom message from store
  clearCustomMessage() {
    this.customMessage = null;
    this._stateRx.customMessage.rxNext(null);
  }

  // Gets current GTM custom message
  getCustomMessage() {
    return this.customMessage;
  }
}
